import * as THREE from "three";

/**
 * PlayStation-era surface shader:
 *  - vertex snapping to a coarse screen grid (the wobble),
 *  - affine (non-perspective-correct) texture mapping (the swim),
 *  - nearest-filtered tiny textures, per-vertex lighting, hard distance fog.
 * Colour quantisation + dithering happen in the PsxComposer post pass, not here.
 */
const VERT = /* glsl */ `
uniform vec2 uSnap;       // snap grid = render target resolution / 2 (coarser = more wobble)
uniform vec3 uLightDir;
uniform vec3 uLightColor;
uniform vec3 uAmbient;
uniform vec3 uScreenPos;    // world position of the CRT face — the screen is the room's lamp
uniform vec3 uScreenColor;
uniform float uScreenPower;
uniform vec3 uDeskPos;      // second practical: the strip under the bezel that lights the controls
uniform vec3 uDeskColor;
uniform float uDeskPower;
uniform vec2 uUvScale;
varying vec2 vUvW;        // uv * w  → divided by vW in the fragment = affine interpolation
varying float vW;
varying vec3 vLight;
varying float vFog;
void main() {
  vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  // vertex snapping in NDC
  vec2 ndc = clip.xy / clip.w;
  ndc = floor(ndc * uSnap) / uSnap;
  clip.xy = ndc * clip.w;
  vW = clip.w;
  vUvW = uv * uUvScale * clip.w;
  vec3 wp = (modelMatrix * vec4(position, 1.0)).xyz;
  vec3 nw = normalize(mat3(modelMatrix) * normal);
  float nd = max(dot(nw, normalize(uLightDir)), 0.0);
  vec3 toScreen = uScreenPos - wp;
  float dist = length(toScreen);
  float lam = max(dot(nw, toScreen / max(dist, 0.001)), 0.0);
  float att = uScreenPower / (1.0 + dist * dist * 2.5);
  vec3 toDesk = uDeskPos - wp;
  float dd = length(toDesk);
  float dlam = max(dot(nw, toDesk / max(dd, 0.001)), 0.0);
  float datt = uDeskPower / (1.0 + dd * dd * 3.2);
  vLight = uAmbient + uLightColor * nd + uScreenColor * att * (0.3 + 0.7 * lam) + uDeskColor * datt * (0.25 + 0.75 * dlam);
  vFog = clamp((-(modelViewMatrix * vec4(position,1.0)).z - 3.0) / 12.0, 0.0, 1.0);
  gl_Position = clip;
}`;
const FRAG = /* glsl */ `
precision mediump float;
uniform sampler2D uMap;
uniform vec3 uFogColor;
uniform vec3 uTint;
varying vec2 vUvW; varying float vW; varying vec3 vLight; varying float vFog;
void main() {
  vec2 uv = vUvW / vW;
  vec3 c = texture2D(uMap, uv).rgb * uTint * vLight;
  c = mix(c, uFogColor, vFog);
  gl_FragColor = vec4(c, 1.0);
}`;

export interface PsxMaterialOptions { map: THREE.Texture; uvScale?: [number, number]; tint?: THREE.ColorRepresentation }

export function psxMaterial(opts: PsxMaterialOptions, shared: PsxUniforms): THREE.ShaderMaterial {
  // Crisp texels up close, but mipmapped at distance: raw nearest-everywhere shimmers as the camera
  // moves, which reads as bad motion rather than as period charm.
  opts.map.magFilter = THREE.NearestFilter;
  opts.map.minFilter = THREE.NearestMipmapLinearFilter;
  opts.map.generateMipmaps = true;
  opts.map.anisotropy = 4;
  opts.map.wrapS = opts.map.wrapT = THREE.RepeatWrapping;
  return new THREE.ShaderMaterial({
    vertexShader: VERT, fragmentShader: FRAG,
    uniforms: {
      uMap: { value: opts.map },
      uUvScale: { value: new THREE.Vector2(...(opts.uvScale ?? [1, 1])) },
      uTint: { value: new THREE.Color(opts.tint ?? 0xffffff) },
      uSnap: shared.uSnap, uLightDir: shared.uLightDir, uLightColor: shared.uLightColor, uAmbient: shared.uAmbient, uFogColor: shared.uFogColor,
      uDeskPos: shared.uDeskPos, uDeskColor: shared.uDeskColor, uDeskPower: shared.uDeskPower,
      uScreenPos: shared.uScreenPos, uScreenColor: shared.uScreenColor, uScreenPower: shared.uScreenPower,
    },
  });
}

export interface PsxUniforms {
  uSnap: THREE.IUniform<THREE.Vector2>; uLightDir: THREE.IUniform<THREE.Vector3>; uLightColor: THREE.IUniform<THREE.Color>;
  uAmbient: THREE.IUniform<THREE.Color>; uFogColor: THREE.IUniform<THREE.Color>;
  uDeskPos: THREE.IUniform<THREE.Vector3>; uDeskColor: THREE.IUniform<THREE.Color>; uDeskPower: THREE.IUniform<number>;
  uScreenPos: THREE.IUniform<THREE.Vector3>; uScreenColor: THREE.IUniform<THREE.Color>; uScreenPower: THREE.IUniform<number>;
}
/**
 * `snapScale` softens the vertex wobble independently of the render resolution: 1 is authentic
 * hardware (violent), ~2.5 keeps the character without the geometry crawling as you look around.
 */
export function sharedUniforms(width: number, height: number, ambient = 0.42, snapScale = 2.5): PsxUniforms {
  return {
    uSnap: { value: new THREE.Vector2((width / 2) * snapScale, (height / 2) * snapScale) },
    uLightDir: { value: new THREE.Vector3(-0.5, 0.7, 0.6) },
    uLightColor: { value: new THREE.Color(1.15, 0.95, 0.65) },  // one caged amber work light
    uAmbient: { value: new THREE.Color(ambient, ambient * 1.08, ambient * 1.06) }, // dark, but you can see the room
    // The console's own practical. Rooms move it to wherever their instrument bank is; without it
    // the screen is the only lamp and everything below the bezel falls to pure ambient, i.e. black.
    uDeskPos: { value: new THREE.Vector3(0, 0, 0) },
    uDeskColor: { value: new THREE.Color(0, 0, 0) },
    uDeskPower: { value: 0 },
    uFogColor: { value: new THREE.Color(0.03, 0.035, 0.035) },
    uScreenPos: { value: new THREE.Vector3(0, 0, 0) },
    uScreenColor: { value: new THREE.Color(0.35, 0.9, 0.45) },
    uScreenPower: { value: 1.1 },
  };
}

/** Emissive, unlit surface (screens, lamps). Still snapped + affine so it sits in the same world. */
export function psxEmissive(map: THREE.Texture, shared: PsxUniforms, tint: THREE.ColorRepresentation = 0xffffff): THREE.ShaderMaterial {
  const m = psxMaterial({ map, tint }, shared);
  m.uniforms.uAmbient = { value: new THREE.Color(1, 1, 1) };
  m.uniforms.uLightColor = { value: new THREE.Color(0, 0, 0) };
  m.uniforms.uScreenColor = { value: new THREE.Color(0, 0, 0) }; // emissive surfaces are their own light
  m.uniforms.uDeskColor = { value: new THREE.Color(0, 0, 0) };
  return m;
}
